import type { AxiosInstance, AxiosError } from 'axios';
import { jwtDecode } from 'jwt-decode';

// Shared, framework-agnostic token-refresh logic for both frontends.
// The two apps differ only by endpoint prefix (/api vs /api/v1) and the
// logout/redirect mechanism, which are passed in.

interface RefreshOptions {
  client: AxiosInstance;
  refreshUrl: string;
}

// Single-flight: while a refresh is in progress, concurrent callers (other
// failed requests and the proactive timer) await the same promise so only one
// /refresh request is ever sent.
let refreshPromise: Promise<string> | null = null;

async function doRefresh({ client, refreshUrl }: RefreshOptions): Promise<string> {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const response = await client.post(refreshUrl, { refreshToken });
  const { token, refreshToken: newRefreshToken } = response.data;

  localStorage.setItem('token', token);
  // Backend rotates the refresh token on every refresh.
  if (newRefreshToken) {
    localStorage.setItem('refreshToken', newRefreshToken);
  }
  client.defaults.headers.common['Authorization'] = `Bearer ${token}`;

  return token;
}

// Returns the new access token. Safe to call concurrently — collapses into one
// in-flight request. Throws if there is no refresh token or refresh fails.
export function refreshAuthTokens(options: RefreshOptions): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = doRefresh(options).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

interface InterceptorOptions {
  refreshUrl: string;
  onLogout: () => void;
}

// Reactive recovery: on a 401/403 auth failure, refresh once and retry the
// original request; on failure, clear the session via onLogout().
export function attachAuthInterceptor(
  client: AxiosInstance,
  { refreshUrl, onLogout }: InterceptorOptions
): void {
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as
        | (typeof error.config & { _retry?: boolean })
        | undefined;
      const status = error.response?.status;

      if (!originalRequest || (status !== 401 && status !== 403)) {
        return Promise.reject(error);
      }

      // Never try to refresh the refresh call itself (avoids infinite loop).
      if (
        typeof originalRequest.url === 'string' &&
        originalRequest.url.endsWith(refreshUrl)
      ) {
        return Promise.reject(error);
      }

      // Only retry once.
      if (originalRequest._retry) {
        return Promise.reject(error);
      }

      if (!localStorage.getItem('refreshToken')) {
        onLogout();
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      try {
        const token = await refreshAuthTokens({ client, refreshUrl });
        originalRequest.headers = originalRequest.headers ?? {};
        originalRequest.headers['Authorization'] = `Bearer ${token}`;
        return client(originalRequest);
      } catch (refreshError) {
        onLogout();
        return Promise.reject(refreshError);
      }
    }
  );
}

// Proactive scheduling helper: milliseconds until we should refresh, computed
// from the JWT `exp` claim minus a safety buffer. Returns null if the token
// can't be decoded or has no expiry (caller should skip proactive refresh; the
// reactive interceptor still covers expiry).
export function msUntilRefresh(
  token: string,
  bufferSeconds = 60
): number | null {
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token);
    if (!exp) {
      return null;
    }
    return exp * 1000 - Date.now() - bufferSeconds * 1000;
  } catch {
    return null;
  }
}
